import { useEffect, useMemo, useState } from "react";
import { ClipboardPaste } from "lucide-react";
import type React from "react";

import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { canReadClipboardText, readClipboardText } from "@/utils/clipboardText";
import { CAPTION, ROW_TITLE } from "@/lib/typography";
import { cn } from "@/lib/utils";

type AutoPastePreference = "enabled" | "disabled";

export interface ClipboardUrlInputRowProps {
  /** Whether the parent modal is open (used for auto-paste timing). */
  active: boolean;
  /** Current input value. */
  value: string;
  /** Update input value. */
  onChange: (value: string) => void;
  /** Optional: clear error state when input changes. */
  onClearError?: () => void;
  /** Optional: surface clipboard errors to the user (toast, inline error, etc.). */
  onUserError?: (message: string) => void;
  /** Disable input + clipboard interactions. */
  disabled?: boolean;
  /**
   * Disable reading from the clipboard (button + auto-paste).
   * Use this when the input is prefilled (e.g. initialUrl) and you don't want
   * clipboard actions to overwrite it.
   */
  disableClipboardRead?: boolean;
  /** Optional input ref (for focusing, selection, etc.). */
  inputRef?: React.RefObject<HTMLInputElement | null>;
  /** Input id attribute. */
  inputId: string;
  /** Placeholder text. */
  placeholder: string;
  /** Optional onFocus handler (e.g., mobile keyboard helper). */
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  /**
   * Preference key shared across modals.
   * Defaults to the same key used by AddContentModal.
   */
  preferenceKey?: string;
  /**
   * Transform pasted clipboard text before setting it (e.g. resolve/normalize URLs).
   * Return null/empty string to ignore.
   */
  transformPastedText?: (raw: string) => string | null;
  /** Hide the one-time auto-paste prompt entirely. */
  hideAutoPastePrompt?: boolean;
}

const DEFAULT_PREFERENCE_KEY = "stasht:addContent:autoPaste";

const readPreference = (key: string): AutoPastePreference | null => {
  try {
    if (typeof window === "undefined") return null;
    const stored = window.localStorage.getItem(key);
    return stored === "enabled" || stored === "disabled" ? (stored as AutoPastePreference) : null;
  } catch {
    return null;
  }
};

const writePreference = (key: string, pref: AutoPastePreference) => {
  try {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(key, pref);
    }
  } catch {
    // ignore storage errors (private mode, disabled storage, etc.)
  }
};

export function ClipboardUrlInputRow({
  active,
  value,
  onChange,
  onClearError,
  onUserError,
  disabled = false,
  disableClipboardRead = false,
  inputRef,
  inputId,
  placeholder,
  onFocus,
  preferenceKey = DEFAULT_PREFERENCE_KEY,
  transformPastedText,
  hideAutoPastePrompt = false,
}: ClipboardUrlInputRowProps) {
  const canReadClipboard = canReadClipboardText();
  const clipboardEnabled = canReadClipboard && !disabled && !disableClipboardRead;

  const [autoPastePreference, setAutoPastePreference] = useState<AutoPastePreference | null>(() =>
    readPreference(preferenceKey),
  );

  // Re-sync preference when opening (matches prior modal behavior).
  useEffect(() => {
    if (!active) return;
    setAutoPastePreference(readPreference(preferenceKey));
  }, [active, preferenceKey]);

  const showAutoPastePrompt = useMemo(() => {
    if (hideAutoPastePrompt) return false;
    if (!clipboardEnabled) return false;
    return autoPastePreference === null;
  }, [autoPastePreference, clipboardEnabled, hideAutoPastePrompt]);

  const applyValue = (next: string) => {
    onChange(next);
    onClearError?.();
    inputRef?.current?.focus();
  };

  const transform = (raw: string): string | null => {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    // Default behavior: if no transform is provided, do not auto-paste arbitrary clipboard text.
    // Each consumer should supply a URL-specific transform (resolve/normalize + validation).
    if (!transformPastedText) return null;

    const next = transformPastedText(trimmed);
    const finalText = next?.trim();
    return finalText ? finalText : null;
  };

  const pasteFromClipboard = async (opts?: { enableAutoPaste?: boolean; fromUser?: boolean }) => {
    try {
      const clipboardText = await readClipboardText();
      if (opts?.enableAutoPaste) {
        setAutoPastePreference("enabled");
        writePreference(preferenceKey, "enabled");
      }
      if (!clipboardText) return;
      const next = transform(clipboardText);
      if (!next) return;
      applyValue(next);
    } catch {
      if (opts?.enableAutoPaste) {
        onUserError?.("Please allow clipboard access to enable auto-paste.");
      } else if (opts?.fromUser) {
        onUserError?.("Couldn't access your clipboard.");
      }
    }
  };

  const disableAutoPaste = () => {
    setAutoPastePreference("disabled");
    writePreference(preferenceKey, "disabled");
  };

  // If user has opted in, try to auto-paste on open (best-effort).
  useEffect(() => {
    if (!active) return;
    if (!clipboardEnabled) return;
    if (autoPastePreference !== "enabled") return;
    if (value.trim()) return;

    const id = window.setTimeout(() => {
      void pasteFromClipboard();
    }, 200);

    return () => window.clearTimeout(id);
  }, [active, autoPastePreference, clipboardEnabled, value]);

  return (
    <div className="grid gap-4">
      {showAutoPastePrompt && (
        <Alert className="border-border bg-muted/50">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className={cn(ROW_TITLE, "mb-1")}>Auto-paste links?</p>
              <p className={CAPTION}>We&apos;ll drop in copied links for you. You can still edit.</p>
            </div>
            <div className="flex gap-2 flex-shrink-0 sm:mt-0 mt-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => void pasteFromClipboard({ enableAutoPaste: true, fromUser: true })}
                className="h-8 px-3"
              >
                Enable
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={disableAutoPaste} className="h-8 px-3">
                Not now
              </Button>
            </div>
          </div>
        </Alert>
      )}

      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-10 w-10 flex-shrink-0 text-muted-foreground"
          onClick={() => void pasteFromClipboard({ fromUser: true })}
          disabled={!clipboardEnabled}
          aria-label="Paste from clipboard"
          title="Paste from clipboard"
        >
          <ClipboardPaste className="h-4 w-4" />
        </Button>

        <Input
          ref={inputRef}
          id={inputId}
          type="text"
          inputMode="url"
          autoComplete="off"
          placeholder={placeholder}
          className="flex-1"
          value={value}
          disabled={disabled}
          onChange={(e) => {
            onChange(e.target.value);
            onClearError?.();
          }}
          onPaste={(e) => {
            // Replace the entire field on paste (avoid appending at caret position).
            const pasted = e.clipboardData?.getData("text") ?? "";
            const next = transform(pasted);
            if (!next) {
              // Block pasting arbitrary/non-URL text into a URL field.
              e.preventDefault();
              onUserError?.("Please paste a valid URL.");
              return;
            }

            e.preventDefault();
            applyValue(next);

            // Keep cursor at end after controlled update.
            window.requestAnimationFrame(() => {
              inputRef?.current?.setSelectionRange(next.length, next.length);
            });
          }}
          onFocus={onFocus}
          required
        />
      </div>
    </div>
  );
}

